import { Box, Stack, Typography } from '@mui/material';
import { ReactNode, useMemo } from 'react';

import { ArrowDownIcon } from '@/components/icons/ArrowDownIcon';
import { CoinsHandIcon } from '@/components/icons/CoinsHandIcon';
import { File05Icon } from '@/components/icons/File05Icon';
import { Users01Icon } from '@/components/icons/Users01Icon';
import { FullScreenModal } from '@/components/modals/FullScreenModal/FullScreenModal';
import {
  SegmentProps,
  StepTracker,
  StepTrackerVariant,
} from '@/components/navigation/StepTracker/StepTracker';
import { TrowserKey } from '@/modules/navigation/trowsers/trowser.types';
import { COLORS } from '@/styles/tokens/colors';

import { ManageIntegrationDataHeaderActions } from './components/ManageIntegrationDataHeaderActions';
import { useManageIntegrationDataContext } from './context/manageIntegrationData.context';
import {
  ManageIntegrationDataProvider,
  ManageIntegrationDataProviderProps,
} from './context/ManageIntegrationData.provider';
import {
  getStepStatus,
  isImportDataPane,
  MANAGE_INTEGRATION_DATA_STEP_LABELS,
  MANAGE_INTEGRATION_DATA_STEP_ORDER,
  ManageIntegrationDataPane,
  ManageIntegrationDataStep,
} from './ManageIntegrationDataModal.types';
import { ImportDataStep } from './steps/importData/ImportDataStep';
import { LinkHoldingsStep } from './steps/linkData/LinkHoldingsStep';
import { LinkTrustsStep } from './steps/linkData/LinkTrustsStep';
import { SelectClientsPane } from './steps/selectClients/SelectClientsPane';

declare module '@/modules/navigation/trowsers/trowser.types' {
  interface TrowserDataMap {
    [TrowserKey.LinkHouseholdData]: {
      section?: ManageIntegrationDataPane;
    };
  }
}

export interface ManageIntegrationDataModalProps
  extends ManageIntegrationDataProviderProps {
  isOpen: boolean;
}

const STEP_ICONS: Record<ManageIntegrationDataStep, ReactNode> = {
  [ManageIntegrationDataStep.SelectClients]: <Users01Icon size={16} />,
  [ManageIntegrationDataStep.LinkTrusts]: <File05Icon size={16} />,
  [ManageIntegrationDataStep.LinkHoldings]: <CoinsHandIcon size={16} />,
  [ManageIntegrationDataStep.ImportData]: <ArrowDownIcon size={16} />,
};

function ManageIntegrationDataModalContent({ isOpen }: { isOpen: boolean }) {
  const {
    activePane,
    activeStepIndex,
    currentStep,
    isFirstStep,
    isLastStep,
    isSaving,
    goToStepIndex,
    goToPreviousStep,
    goToNextStep,
    commitAndClose,
    closeModal,
    selectClientsForm,
  } = useManageIntegrationDataContext();

  const segments = useMemo<SegmentProps[]>(
    () =>
      MANAGE_INTEGRATION_DATA_STEP_ORDER.map((step, index) => ({
        display: MANAGE_INTEGRATION_DATA_STEP_LABELS[step],
        status: getStepStatus(index, activeStepIndex),
        icon: STEP_ICONS[step],
        onClick: () => goToStepIndex(index),
      })),
    [activeStepIndex, goToStepIndex]
  );

  return (
    <FullScreenModal
      trowserKey={TrowserKey.LinkHouseholdData}
      isOpen={isOpen}
      onClose={closeModal}
    >
      {/* A bounded flex column so the header and the tracker banner stay fixed
          while the body below them scrolls. */}
      <Stack height="100%" overflow="hidden">
        <Stack
          component="header"
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            borderBottom: `solid 1px ${COLORS.GRAY[200]}`,
            p: 2,
            flexShrink: 0,
          }}
        >
          <Typography variant="title-l" component="h1">
            Manage integration data
          </Typography>

          <ManageIntegrationDataHeaderActions
            onPrevious={goToPreviousStep}
            onNext={goToNextStep}
            onCancel={closeModal}
            onDone={commitAndClose}
            isFirstStep={isFirstStep}
            isLastStep={isLastStep}
            isSaving={isSaving}
          />
        </Stack>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            bgcolor: COLORS.BLUE[50],
            borderBottom: `solid 1px ${COLORS.GRAY[200]}`,
            p: 3,
            flexShrink: 0,
          }}
        >
          <StepTracker
            variant={StepTrackerVariant.Large}
            segments={segments}
            activeSegmentIndex={activeStepIndex}
            disableNavigation={isSaving}
          />
        </Box>

        <Box sx={{ p: 2, flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {currentStep === ManageIntegrationDataStep.SelectClients && (
            <SelectClientsPane form={selectClientsForm} />
          )}

          {currentStep === ManageIntegrationDataStep.LinkTrusts && (
            <LinkTrustsStep />
          )}

          {currentStep === ManageIntegrationDataStep.LinkHoldings && (
            <LinkHoldingsStep />
          )}

          {currentStep === ManageIntegrationDataStep.ImportData && (
            <ImportDataStep
              activePane={
                isImportDataPane(activePane)
                  ? activePane
                  : ManageIntegrationDataPane.ImportDataEntities
              }
            />
          )}
        </Box>
      </Stack>
    </FullScreenModal>
  );
}

/**
 * The "Manage integration data" wizard.
 *
 * A full-screen trowser on `TrowserKey.LinkHouseholdData`: the visible pane is
 * the trowser's `section`, so the wizard deep-links and survives reload.
 *
 * Moving forward commits each traversed step; moving back never writes. "Done"
 * commits the step on screen and closes.
 */
export function ManageIntegrationDataModal({
  isOpen,
  ...providerProps
}: ManageIntegrationDataModalProps) {
  return (
    <ManageIntegrationDataProvider {...providerProps}>
      <ManageIntegrationDataModalContent isOpen={isOpen} />
    </ManageIntegrationDataProvider>
  );
}
